import type { JobAttachment, JobAttachmentLink } from '../api/types';
import { getOfflineDb, type SavedAttachmentFile } from './db';

/**
 * Job attachments saved on the device (CAPTURE_JOBS.md section 8.5), so an
 * operator in an aisle with no signal can still open the rows to recapture
 * or the drawing the manager attached.
 *
 * Whenever the job view loads a job's list with signal, it saves the list
 * and then every file the device does not hold yet, one at a time. A saved
 * file is opened from the device even when online: the bytes are the same,
 * and the tap works without waiting on the network.
 *
 * Space is bounded. All saved files together stay under
 * `ATTACHMENT_BUDGET_BYTES`, and under what the browser reports as free.
 * When a file does not fit, files of other jobs are dropped first, least
 * recently used first. Files of the job being saved are never dropped to
 * make room for each other, so a job too large for the budget saves as much
 * of itself as fits and marks the rest as not saved.
 */

/** All saved attachments together, across every job. */
export const ATTACHMENT_BUDGET_BYTES = 250 * 1024 * 1024;

export type SavedState = 'saved' | 'saving' | 'no-space' | 'failed';

export interface SaveProgress {
  attachmentId: string;
  state: SavedState;
}

/** Keeps the list for a restart with no signal, without its expiring URLs. */
export async function saveAttachmentList(jobId: string, items: JobAttachmentLink[]): Promise<void> {
  const db = await getOfflineDb();
  const plain: JobAttachment[] = items.map(
    ({ attachmentId, fileName, contentType, byteLength, uploadedBy, uploadedAt }) => ({
      attachmentId,
      fileName,
      contentType,
      byteLength,
      uploadedBy,
      uploadedAt,
    })
  );
  await db.put('attachmentLists', { jobId, items: plain, savedAt: new Date().toISOString() });
}

export async function readAttachmentList(
  jobId: string
): Promise<{ items: JobAttachment[]; savedAt: string } | null> {
  const db = await getOfflineDb();
  const record = await db.get('attachmentLists', jobId);
  return record ? { items: record.items, savedAt: record.savedAt } : null;
}

/** The ids of this job's files the device holds. */
export async function savedAttachmentIds(jobId: string): Promise<Set<string>> {
  const db = await getOfflineDb();
  const keys = await db.getAllKeysFromIndex('attachmentFiles', 'by-job', jobId);
  return new Set(keys);
}

/** A saved file as a Blob ready to open, or null when the device does not hold it. */
export async function readSavedAttachment(attachmentId: string): Promise<Blob | null> {
  const db = await getOfflineDb();
  const record = await db.get('attachmentFiles', attachmentId);
  if (!record) return null;
  record.lastUsedAt = new Date().toISOString();
  await db.put('attachmentFiles', record);
  return new Blob([record.bytes], { type: record.contentType });
}

async function savedBytesTotal(): Promise<number> {
  const db = await getOfflineDb();
  let total = 0;
  let cursor = await db.transaction('attachmentFiles').store.openCursor();
  while (cursor) {
    total += cursor.value.byteLength;
    cursor = await cursor.continue();
  }
  return total;
}

/** Free space the browser reports, or Infinity where it reports none. */
async function browserFreeBytes(): Promise<number> {
  try {
    const estimate = await navigator.storage?.estimate?.();
    if (estimate?.quota === undefined || estimate.usage === undefined) return Infinity;
    return estimate.quota - estimate.usage;
  } catch {
    return Infinity;
  }
}

/**
 * Drops saved files of other jobs, least recently used first, until `needed`
 * more bytes fit under the budget. Answers whether they now fit.
 */
async function makeRoom(needed: number, keepJobId: string): Promise<boolean> {
  let total = await savedBytesTotal();
  if (total + needed <= ATTACHMENT_BUDGET_BYTES) return true;
  const db = await getOfflineDb();
  const others = (await db.getAll('attachmentFiles'))
    .filter((file) => file.jobId !== keepJobId)
    .sort((a, b) => a.lastUsedAt.localeCompare(b.lastUsedAt));
  for (const file of others) {
    await db.delete('attachmentFiles', file.attachmentId);
    total -= file.byteLength;
    if (total + needed <= ATTACHMENT_BUDGET_BYTES) return true;
  }
  return false;
}

let persistAsked = false;

/** Asks the browser once not to clear saved data under storage pressure. */
function askForPersistentStorage(): void {
  if (persistAsked) return;
  persistAsked = true;
  void navigator.storage?.persist?.().catch(() => undefined);
}

/**
 * Saves every listed file the device does not hold yet, and forgets saved
 * files of this job that are no longer listed. Reports each file's state
 * through `onProgress` as it changes. Runs one download at a time, so a job
 * with many large files does not hold them all in memory at once.
 */
export async function saveJobAttachments(
  jobId: string,
  items: JobAttachmentLink[],
  onProgress: (progress: SaveProgress) => void,
  download: (url: string) => Promise<ArrayBuffer> = defaultDownload
): Promise<void> {
  const db = await getOfflineDb();
  const listed = new Set(items.map((item) => item.attachmentId));
  const held = await savedAttachmentIds(jobId);
  for (const id of held) {
    if (!listed.has(id)) await db.delete('attachmentFiles', id);
  }

  askForPersistentStorage();
  for (const item of items) {
    if (held.has(item.attachmentId)) {
      onProgress({ attachmentId: item.attachmentId, state: 'saved' });
      continue;
    }
    const fits =
      item.byteLength <= ATTACHMENT_BUDGET_BYTES &&
      (await makeRoom(item.byteLength, jobId)) &&
      (await browserFreeBytes()) > item.byteLength;
    if (!fits) {
      onProgress({ attachmentId: item.attachmentId, state: 'no-space' });
      continue;
    }
    onProgress({ attachmentId: item.attachmentId, state: 'saving' });
    try {
      const bytes = await download(item.downloadUrl);
      const now = new Date().toISOString();
      const record: SavedAttachmentFile = {
        attachmentId: item.attachmentId,
        jobId,
        fileName: item.fileName,
        contentType: item.contentType,
        byteLength: bytes.byteLength,
        bytes,
        savedAt: now,
        lastUsedAt: now,
      };
      await db.put('attachmentFiles', record);
      onProgress({ attachmentId: item.attachmentId, state: 'saved' });
    } catch {
      onProgress({ attachmentId: item.attachmentId, state: 'failed' });
    }
  }
}

async function defaultDownload(url: string): Promise<ArrayBuffer> {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`Download failed with ${response.status}`);
  return response.arrayBuffer();
}
